import {
  EHeatMapCategory,
  IPolygonProperties,
} from '../../../types-library/src'
import { Expression } from 'mapbox-gl'
import { presenter } from "@wiggot/core-apis"

export const heatMapColors = {
  [EHeatMapCategory.PRICE]: {
    very_low: '#D0FBDF',
    low: '#A4F6DF',
    medium_low: '#5EEFDF',
    medium_high: '#33C5E1',
    high: '#3483E3',
    very_high: '#345BE5',
    hover: '#00504E',
  },
  [EHeatMapCategory.RENT]: {
    very_low: '#F1D9FF',
    low: '#DFB0FF',
    medium_low: '#C981FF',
    medium_high: '#B85AFF',
    high: '#A633FF',
    very_high: '#8F01FF',
    hover: '#360587',
  },
  [EHeatMapCategory.CAPRATE]: {
    very_low: '#FEFDDB',
    low: '#F6E699',
    medium_low: '#EDCB4F',
    medium_high: '#EBA72B',
    high: '#EE731C',
    very_high: '#F0340A',
    hover: '#000000',
  },
}

export const heatMapCaseExpressionColors: {
  [key in EHeatMapCategory]: Expression
} = {
  [EHeatMapCategory.PRICE]: [
    'case',
    ['==', ['get', 'price_index_rank'], 'very_low'],
    heatMapColors.price.very_low,
    ['==', ['get', 'price_index_rank'], 'low'],
    heatMapColors.price.low,
    ['==', ['get', 'price_index_rank'], 'medium_low'],
    heatMapColors.price.medium_low,
    ['==', ['get', 'price_index_rank'], 'medium_high'],
    heatMapColors.price.medium_high,
    ['==', ['get', 'price_index_rank'], 'high'],
    heatMapColors.price.high,
    ['==', ['get', 'price_index_rank'], 'very_high'],
    heatMapColors.price.very_high,
    'white',
  ],
  [EHeatMapCategory.RENT]: [
    'case',
    ['==', ['get', 'rent_index_rank'], 'very_low'],
    heatMapColors.rent.very_low,
    ['==', ['get', 'rent_index_rank'], 'low'],
    heatMapColors.rent.low,
    ['==', ['get', 'rent_index_rank'], 'medium_low'],
    heatMapColors.rent.medium_low,
    ['==', ['get', 'rent_index_rank'], 'medium_high'],
    heatMapColors.rent.medium_high,
    ['==', ['get', 'rent_index_rank'], 'high'],
    heatMapColors.rent.high,
    ['==', ['get', 'rent_index_rank'], 'very_high'],
    heatMapColors.rent.very_high,
    'white',
  ],
  [EHeatMapCategory.CAPRATE]: [
    'case',
    ['==', ['get', 'caprate_index_rank'], 'very_low'],
    heatMapColors.caprate.very_low,
    ['==', ['get', 'caprate_index_rank'], 'low'],
    heatMapColors.caprate.low,
    ['==', ['get', 'caprate_index_rank'], 'medium_low'],
    heatMapColors.caprate.medium_low,
    ['==', ['get', 'caprate_index_rank'], 'medium_high'],
    heatMapColors.caprate.medium_high,
    ['==', ['get', 'caprate_index_rank'], 'high'],
    heatMapColors.caprate.high,
    ['==', ['get', 'caprate_index_rank'], 'very_high'],
    heatMapColors.caprate.very_high,
    'white',
  ],
}

export const createInterpolateCaseColor = (params: {
  previousCategory: EHeatMapCategory
  nextCategory: EHeatMapCategory
  progress: number
}): any[] => {
  const { previousCategory, nextCategory, progress } = params

  let condeIndex = 'price_index_rank'

  if (nextCategory === EHeatMapCategory.RENT) {
    condeIndex = 'rent_index_rank'
  } else if (nextCategory === EHeatMapCategory.CAPRATE) {
    condeIndex = 'caprate_index_rank'
  }

  return [
    'match',
    ['get', condeIndex],
    'very_low',
    interpolateColor(
      heatMapColors[previousCategory].very_low,
      heatMapColors[nextCategory].very_low,
      progress
    ),
    'low',
    interpolateColor(
      heatMapColors[previousCategory].low,
      heatMapColors[nextCategory].low,
      progress
    ),
    'medium_low',
    interpolateColor(
      heatMapColors[previousCategory].medium_low,
      heatMapColors[nextCategory].medium_low,
      progress
    ),
    'medium_high',
    interpolateColor(
      heatMapColors[previousCategory].medium_high,
      heatMapColors[nextCategory].medium_high,
      progress
    ),
    'high',
    interpolateColor(
      heatMapColors[previousCategory].high,
      heatMapColors[nextCategory].high,
      progress
    ),
    'very_high',
    interpolateColor(
      heatMapColors[previousCategory].very_high,
      heatMapColors[nextCategory].very_high,
      progress
    ),
    'white',
  ]
}

export const createInterpolateHoverCaseColor = (params: {
  category: EHeatMapCategory
  progress: number
}): any[] => {
  const { category, progress } = params

  let condeIndex = 'price_index_rank'

  if (category === EHeatMapCategory.RENT) {
    condeIndex = 'rent_index_rank'
  } else if (category === EHeatMapCategory.CAPRATE) {
    condeIndex = 'caprate_index_rank'
  }

  return [
    'match',
    ['get', condeIndex],
    'very_low',
    interpolateColor(
      heatMapColors[category].very_low,
      heatMapColors[category].hover,
      progress
    ),
    'low',
    interpolateColor(
      heatMapColors[category].low,
      heatMapColors[category].hover,
      progress
    ),
    'medium_low',
    interpolateColor(
      heatMapColors[category].medium_low,
      heatMapColors[category].hover,
      progress
    ),
    'medium_high',
    interpolateColor(
      heatMapColors[category].medium_high,
      heatMapColors[category].hover,
      progress
    ),
    'high',
    interpolateColor(
      heatMapColors[category].high,
      heatMapColors[category].hover,
      progress
    ),
    'very_high',
    interpolateColor(
      heatMapColors[category].very_high,
      heatMapColors[category].hover,
      progress
    ),
    'white',
  ]
}

/**
 * Interpolates between two colors in hex format.
 * @param startColor - The starting color in hex format (e.g., "#FF0000").
 * @param endColor - The ending color in hex format (e.g., "#00FF00").
 * @param progress - A number between 0 and 1 indicating the interpolation progress.
 * @returns The interpolated color in hex format.
 */
const interpolateColor = (
  startColor: string,
  endColor: string,
  progress: number
): string => {
  const start = parseColor(startColor)
  const end = parseColor(endColor)

  const r = Math.round(start.r + (end.r - start.r) * progress)
  const g = Math.round(start.g + (end.g - start.g) * progress)
  const b = Math.round(start.b + (end.b - start.b) * progress)

  return rgbToHex(r, g, b)
}

/**
 * Parses a hex color string to an RGB object.
 * @param color - The hex color string (e.g., "#FF0000").
 * @returns An object with r, g, and b properties.
 */
const parseColor = (color: string): { r: number; g: number; b: number } => {
  const hex = color.replace('#', '')
  const bigint = parseInt(hex, 16)
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  }
}

/**
 * Converts RGB values to a hex color string.
 * @param r - The red component (0-255).
 * @param g - The green component (0-255).
 * @param b - The blue component (0-255).
 * @returns The hex color string.
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  const componentToHex = (c: number): string => {
    const hex = c.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`
}

export const animateChangeHeatMapByCategories = (params: {
  layerName: string
  previousCategory: EHeatMapCategory
  nextCategory: EHeatMapCategory
  duration: number
  map: mapboxgl.Map
}) => {
  const { layerName, previousCategory, nextCategory, duration, map } = params

  let start: number | null = null

  const step = (timestamp: number) => {
    if (!start) {
      start = timestamp
    }
    const progress = Math.min((timestamp - start) / duration, 1)

    const interpolatedColors = createInterpolateCaseColor({
      previousCategory,
      nextCategory,
      progress,
    })

    map.setPaintProperty(layerName, 'fill-color', [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      ['feature-state', 'hover_color'],
      interpolatedColors,
    ])

    if (progress < 1) {
      window.requestAnimationFrame(step)
    }
  }

  window.requestAnimationFrame(step)
}

export const animateChangeHeatMapByColors = (params: {
  layerName: string
  previousColor: string
  nextColor: string
  duration: number
  map: mapboxgl.Map
}) => {
  const { layerName, previousColor, nextColor, duration, map } = params

  let start: number | null = null

  const step = (timestamp: number) => {
    if (!start) {
      start = timestamp
    }
    const progress = Math.min((timestamp - start) / duration, 1)

    const interpolatedColors = interpolateColor(
      previousColor,
      nextColor,
      progress
    )

    map.setPaintProperty(layerName, 'fill-color', [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      ['feature-state', 'hover_color'],
      interpolatedColors,
    ])

    if (progress < 1) {
      window.requestAnimationFrame(step)
    }
  }

  window.requestAnimationFrame(step)
}

const interpolateWidth = (
  startWidth: number,
  endWidth: number,
  progress: number
): number => {
  return startWidth + (endWidth - startWidth) * progress
}

export const animateHoverPolygon = (params: {
  source: string
  sourceLayer: string
  featureId: number
  previousBorder: number
  nextBorder: number
  bgColor: string
  bgHoverColor: string
  duration: number
  map: mapboxgl.Map
}) => {
  const {
    source,
    sourceLayer,
    featureId,
    previousBorder,
    nextBorder,
    bgColor,
    bgHoverColor,
    duration,
    map,
  } = params

  let start: number | null = null

  const step = (timestamp: number) => {
    if (!start) {
      start = timestamp
    }
    const progress = Math.min((timestamp - start) / duration, 1)

    const inHover = interpolateWidth(previousBorder, nextBorder, progress)

    const interpolatedColors = interpolateColor(bgColor, bgHoverColor, progress)

    map?.setFeatureState(
      {
        source,
        sourceLayer,
        id: featureId,
      },
      { hover_width: inHover, hover_color: interpolatedColors }
    )
    if (progress < 1) {
      window.requestAnimationFrame(step)
    }
  }

  window.requestAnimationFrame(step)
}

export const presenterPolygonProperties = (params: {
  properties: any
}): IPolygonProperties => {
  const { properties } = params

  const nomenclature = presenter<IPolygonProperties>({
    value: {
      ...properties,
      centroid: JSON.parse(properties?.centroid),
    },
    style: 'camel',
  })
  return nomenclature
}

export const tileZoomConfiguration = {
  state: {
    minZoom: 9,
    maxZoom: 11,
  },
  municipality: {
    minZoom: 11,
    maxZoom: 13,
  },
  neighborhood: {
    minZoom: 13,
    maxZoom: 15,
  },
  block: {
    minZoom: 15,
    maxZoom: 18,
  },
  property: {
    minZoom: 18,
    maxZoom: 22,
  },
}

export const filterPolygonByUrlCode = (params: {
  map: mapboxgl.Map
  layers: string[]
  urlCode: string
}): void => {
  const { map, layers, urlCode } = params

  for (const layer of layers) {
    const findLayer = map.getLayer(layer)
    if (!findLayer) {
      continue
    }
    map.setFilter(layer, [
      'all',
      ['!=', ['index-of', urlCode, ['get', 'full_url_code']], -1] as any,
    ])
  }
}
